import React, { useEffect, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Options, USERSROLE, OrgTenant, OptionItem } from 'types';
import { FormError } from 'components';
import { Filter16, ZoomInArea16, Close16 } from '@carbon/icons-react';
import {
  DatePicker,
  DatePickerInput,
  Button,
  Form,
  TextInput,
  Checkbox,
  Dropdown,
  Toggle
} from 'carbon-components-react';
import { useFormik } from 'formik';
import { selectOrganisationTenants } from 'redux/member/selectors';
import { SearchEnquiriesSchema } from 'utilities/validation';
import Utils from 'utilities/helper.util';
import { uniq, without, includes, orderBy } from 'lodash';
import AddressSearch from './addressSearch';

const enquiryLodgedOption: Options = [
  {
    label: 'Today',
    value: 'today'
  },
  {
    label: 'This week',
    value: 'this-week'
  },
  {
    label: 'Last 7 days',
    value: '7'
  },
  {
    label: 'Last 30 days',
    value: '30'
  },
  {
    label: 'Last 60 days (default)',
    value: '60'
  },
  {
    label: 'Custom range ...',
    value: 'el-custom'
  }
];

const enquiryLodgedOptionFullSet: Options = [
  {
    label: 'Today (default)',
    value: 'today'
  },
  {
    label: 'This week',
    value: 'this-week'
  },
  {
    label: 'Last 7 days',
    value: '7'
  },
  {
    label: 'Last 30 days',
    value: '30'
  },
  {
    label: 'Custom range ...',
    value: 'el-custom'
  }
];

const jobStartOption: Options = [
  {
    label: 'Next 7 days',
    value: 'n-7'
  },
  {
    label: 'Next 30 days',
    value: 'n-30'
  },
  {
    label: 'Next 90 days',
    value: 'n-90'
  },
  {
    label: 'Last 7 days',
    value: 'l-7'
  },
  {
    label: 'Last 30 days',
    value: 'l-30'
  },
  {
    label: 'Last 90 days',
    value: 'l-90'
  },
  {
    label: 'Custom range ...',
    value: 'js-custom'
  }
];

const getDateOptionItem = (type: string, value: string, isFullSet?: any) => {
  if (type === 'enquiryLodged') {
    if (isFullSet) {
      return enquiryLodgedOptionFullSet.find((o) => value === o.value);
    }
    return enquiryLodgedOption.find((o) => value === o.value);
  } else {
    return jobStartOption.find((o) => value === o.value);
  }
};

const ApplyFilterForm: React.FC<Props> = ({
  isFullSet,
  handleFilter,
  filterValues,
  resetValues,
  setFilterValues,
  handleOpenCloseFilter,
  dateRangeText,
  user,
  parentOverflow,
  disabledAllExceptApply,
  isListView
}: any) => {
  const mapSearchWithExtentZoomRef = useRef(false);
  const orgTenants = useSelector(selectOrganisationTenants);
  const [orgTenantItems, setOrgTenantItems] = useState<OptionItem[]>([]);

  const {
    touched,
    errors,
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    resetForm,
    setFieldValue
  } = useFormik({
    enableReinitialize: true,
    initialValues: filterValues,
    validationSchema: SearchEnquiriesSchema,
    onSubmit: (values: any) => {
      handleFilter(values, mapSearchWithExtentZoomRef.current);
    }
  });

  const handleResetFilter = () => {
    resetValues();
    resetForm();
  };

  const updateFilterValues = useCallback(
    (updatedValues: object) => {
      setFilterValues({
        ...values,
        ...updatedValues
      });
    },
    [values, setFilterValues]
  );

  const getOrgTenantOptionItem = useCallback(
    (value: string) => {
      return orgTenantItems.find((o) => value === o.value);
    },
    [orgTenantItems]
  );

  const handleDropdownChange = ({ selectedItem }: any) => {
    if (selectedItem?.value) {
      switch (selectedItem.value) {
        case 'today': {
          const today = Utils.getNewDate().startOf('day');
          updateFilterValues({
            enquiryLodged: selectedItem.value,
            enquiryLodgedCustomed: false,
            createdAfter: today,
            createdBefore: undefined
          });
          break;
        }
        case 'this-week': {
          const today = Utils.getNewDate();
          const startOfThisWeek = today.startOf('week').add(1, 'day');
          const endOfThisWeek = today.endOf('week').add(1, 'day');
          updateFilterValues({
            enquiryLodged: selectedItem.value,
            enquiryLodgedCustomed: false,
            createdAfter: startOfThisWeek,
            createdBefore: endOfThisWeek
          });
          break;
        }
        case '7':
        case '30':
        case '60': {
          const date = Utils.getDateBeforeDays(
            Number(selectedItem.value)
          ).startOf('day');
          updateFilterValues({
            enquiryLodged: selectedItem.value,
            enquiryLodgedCustomed: false,
            createdAfter: date,
            createdBefore: undefined
          });
          break;
        }
        case 'el-custom': {
          updateFilterValues({
            enquiryLodged: selectedItem.value,
            enquiryLodgedCustomed: false,
            createdAfter: undefined,
            createdBefore: undefined
          });
          break;
        }
        case 'n-7':
        case 'n-30':
        case 'n-90': {
          const days = selectedItem.value.split('-');
          const date = Utils.getDateAfterDays(Number(days[1])).startOf('day');
          const today = Utils.getNewDate().startOf('day');
          updateFilterValues({
            jobStart: selectedItem.value,
            jobStartCustomed: false,
            digStartAfter: today,
            digStartBefore: date
          });
          break;
        }
        case 'l-7':
        case 'l-30':
        case 'l-90': {
          const days = selectedItem.value.split('-');
          const date = Utils.getDateBeforeDays(Number(days[1])).startOf('day');
          const today = Utils.getNewDate().startOf('day');
          updateFilterValues({
            jobStart: selectedItem.value,
            jobStartCustomed: false,
            digStartAfter: date,
            digStartBefore: today
          });
          break;
        }
        case 'js-custom': {
          updateFilterValues({
            jobStart: selectedItem.value,
            jobStartCustomed: false,
            digStartAfter: undefined,
            digStartBefore: undefined
          });
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  const handleStatusChange = (value: string, checked: boolean) => {
    let { status } = values;
    if (checked) {
      status.push(value);
      status = uniq(status);
    } else {
      status = without(status, value);
    }
    updateFilterValues({
      status
    });
  };

  const handleDateChange = (key: string, value: any) => {
    if (Array.isArray(value) && value.length > 0) {
      const updatedValue = value[0];
      updateFilterValues({
        [key]: Utils.getNewDate(updatedValue)
      });
    }
  };

  const translateOptionLabel = (type: string, item: any) => {
    const {
      createdAfter,
      createdBefore,
      digStartAfter,
      digStartBefore,
      enquiryLodgedCustomed,
      jobStartCustomed
    } = values;
    if (type === 'enquiryLodged') {
      return item.value === 'el-custom' && enquiryLodgedCustomed
        ? `${Utils.toLocalDateFormat(createdAfter)} – ${Utils.toLocalDateFormat(
            createdBefore
          )}`
        : item.label;
    } else {
      return item.value === 'js-custom' && jobStartCustomed
        ? `${Utils.toLocalDateFormat(
            digStartAfter
          )} – ${Utils.toLocalDateFormat(digStartBefore)}`
        : item.label;
    }
  };

  const onChangeDate = (key: string, val: any, setField?: boolean) => {
    setField && setFieldValue(key, Utils.getNewDate(val[0]), true);
    handleDateChange(key, val);
  };

  const onCalendarClose = (val: any, strValue: any, self: any) => {
    strValue && self.setDate(strValue, true, 'd/m/Y');
  };

  const onSelectAddress = (item: any) => {
    if (window.loadedView && item.center && item.zoom) {
      window.loadedView.goTo({
        center: item.center,
        zoom: item.zoom
      });
    }
  };

  useEffect(() => {
    const {
      enquiryLodged,
      createdAfter,
      createdBefore,
      digStartAfter,
      digStartBefore,
      jobStart,
      enquiryLodgedCustomed,
      jobStartCustomed
    } = values;
    if (
      enquiryLodged === 'el-custom' &&
      createdAfter &&
      createdBefore &&
      !enquiryLodgedCustomed
    ) {
      updateFilterValues({
        enquiryLodgedCustomed: true
      });
    }
    if (
      jobStart === 'js-custom' &&
      digStartAfter &&
      digStartBefore &&
      !jobStartCustomed
    ) {
      updateFilterValues({
        jobStartCustomed: true
      });
    }
  }, [values, updateFilterValues]);

  useEffect(() => {
    if (Array.isArray(orgTenants) && orgTenants.length > 0) {
      const options = orgTenants.map((item: OrgTenant) => {
        const { id, name } = item;

        return {
          value: String(id),
          label: name.replace('DBYD ', '')
        };
      });
      const items = orderBy(uniq(options), ['label'], ['asc']);
      setOrgTenantItems([
        {
          value: '0',
          label: 'National'
        },
        ...items
      ]);
    }
  }, [orgTenants]);

  return (
    <div
      className={`search-filter-panel-wrapper ${
        parentOverflow ? 'parent-overflow' : ''
      }`}
    >
      <div
        className={'search-filter-panel-button'}
        onClick={
          disabledAllExceptApply
            ? undefined
            : () => handleOpenCloseFilter(false)
        }
      >
        <Filter16 />
      </div>
      <section className="search-filter-panel-main">
        <Form>
          <div className="search-filter-panel-form-wrapper">
            <div className="heading">
              <p>Filter the view</p>
              <Button
                kind="ghost"
                size="sm"
                onClick={
                  disabledAllExceptApply
                    ? (event: any) => {
                        mapSearchWithExtentZoomRef.current = false;
                        handleSubmit(event);
                      }
                    : () => handleOpenCloseFilter(false)
                }
              >
                <Close16 />
              </Button>
            </div>
            {isFullSet && !isListView && (
              <div>
                <AddressSearch
                  onClickResult={(item: any) => onSelectAddress(item)}
                />
              </div>
            )}
            <div className="search-filter-panel-date-container">
              <div className="dropdown-container">
                {isFullSet &&
                  user.role === USERSROLE.DBYDADMIN &&
                  !user?.Organisation?.tenantId && (
                    <>
                      <Dropdown
                        className="apply-filter-form-tenant"
                        id="add-member-tenant"
                        titleText="Organisation territory"
                        label="Choose an option"
                        items={orgTenantItems}
                        onChange={(item) =>
                          item.selectedItem &&
                          updateFilterValues({
                            orgTenant:
                              item.selectedItem.value !== '0'
                                ? item.selectedItem.value
                                : undefined
                          })
                        }
                        selectedItem={
                          values?.orgTenant
                            ? getOrgTenantOptionItem(values.orgTenant)
                            : {
                                value: '0',
                                label: 'National'
                              }
                        }
                      />
                      {touched && errors.orgTenant && (
                        <FormError>{errors.orgTenant}</FormError>
                      )}
                    </>
                  )}
                <Dropdown
                  ariaLabel="Enquiry lodged"
                  id="enquiry-lodged-date"
                  items={
                    isFullSet ? enquiryLodgedOptionFullSet : enquiryLodgedOption
                  }
                  label="Choose an option"
                  titleText="Enquiry lodged"
                  onChange={handleDropdownChange}
                  itemToString={(item) =>
                    translateOptionLabel('enquiryLodged', item)
                  }
                  initialSelectedItem={getDateOptionItem(
                    'enquiryLodged',
                    dateRangeText ? dateRangeText : values.enquiryLodged,
                    isFullSet
                  )}
                />
                {values.enquiryLodged === 'el-custom' &&
                  !values.enquiryLodgedCustomed && (
                    <div className="date-picker-container">
                      <DatePicker
                        dateFormat="d/m/Y"
                        datePickerType="single"
                        value={
                          values.createdAfter
                            ? Utils.toLocalDateFormat(values.createdAfter)
                            : ''
                        }
                        onChange={(val: any) => {
                          onChangeDate('createdAfter', val, false);
                        }}
                        onClose={onCalendarClose}
                      >
                        <DatePickerInput
                          id="createdAfter"
                          name="createdAfter"
                          data-testid="createdAfter"
                          labelText="From"
                          placeholder="dd/mm/yyyy"
                          size={'sm'}
                          type="text"
                          autoComplete="off"
                        />
                      </DatePicker>
                      <DatePicker
                        dateFormat="d/m/Y"
                        datePickerType="single"
                        onChange={(val: any) => {
                          onChangeDate('createdBefore', val, true);
                        }}
                        onClose={onCalendarClose}
                        value={
                          values.createdBefore
                            ? Utils.toLocalDateFormat(values.createdBefore)
                            : ''
                        }
                      >
                        <DatePickerInput
                          id="createdBefore"
                          name="createdBefore"
                          labelText="To"
                          data-testid="createdBefore"
                          placeholder="dd/mm/yyyy"
                          size={'sm'}
                          type="text"
                          autoComplete="off"
                        />
                      </DatePicker>
                    </div>
                  )}
              </div>
              {touched && errors.createdBefore && (
                <FormError>{errors.createdBefore}</FormError>
              )}
              <div className="dropdown-container">
                <Dropdown
                  ariaLabel="Job start dates"
                  id="job-start-date"
                  items={jobStartOption}
                  label="Choose an option"
                  titleText="Job start dates"
                  onChange={handleDropdownChange}
                  itemToString={(item) =>
                    translateOptionLabel('jobStart', item)
                  }
                  initialSelectedItem={getDateOptionItem(
                    'jobStart',
                    values.jobStart
                  )}
                />
                {values.jobStart === 'js-custom' && !values.jobStartCustomed && (
                  <div className="date-picker-container">
                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      onChange={(val: any) =>
                        onChangeDate('digStartAfter', val, false)
                      }
                      onClose={onCalendarClose}
                      value={
                        values.digStartAfter
                          ? Utils.toLocalDateFormat(values.digStartAfter)
                          : ''
                      }
                    >
                      <DatePickerInput
                        id="digStartAfter"
                        name="digStartAfter"
                        data-testid="digStartAfter"
                        labelText="From"
                        placeholder="dd/mm/yyyy"
                        size={'sm'}
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                    <DatePicker
                      dateFormat="d/m/Y"
                      datePickerType="single"
                      onChange={(val: any) =>
                        onChangeDate('digStartBefore', val, true)
                      }
                      onClose={onCalendarClose}
                      value={
                        values.digStartBefore
                          ? Utils.toLocalDateFormat(values.digStartBefore)
                          : ''
                      }
                    >
                      <DatePickerInput
                        id="digStartBefore"
                        name="digStartBefore"
                        labelText="To"
                        data-testid="digStartBefore"
                        placeholder="dd/mm/yyyy"
                        size={'sm'}
                        type="text"
                        autoComplete="off"
                      />
                    </DatePicker>
                  </div>
                )}
              </div>
              {touched && errors.digStartBefore && (
                <FormError>{errors.digStartBefore}</FormError>
              )}
            </div>
            <hr className="divider" />
            <div className="bx--fieldset status-checkbox-container">
              <legend className="bx--label">Status</legend>
              <Checkbox
                labelText="Waiting for responses"
                id="status-checked-waiting"
                onChange={(checked: boolean) =>
                  handleStatusChange('WAITING', checked)
                }
                checked={includes(values.status, 'WAITING')}
              />
              <Checkbox
                labelText="All responses received"
                id="status-checked-received"
                onChange={(checked: boolean) =>
                  handleStatusChange('ALL_RECEIVED', checked)
                }
                checked={includes(values.status, 'ALL_RECEIVED')}
              />
            </div>
            <hr className="divider" />
            {isFullSet ? (
              <div className="search-filter-panel-input-container">
                <div className="filter-input">
                  <TextInput
                    id="jobNumber"
                    name="jobNumber"
                    labelText="Job number"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.jobNumber}
                    data-testid="jobNumber"
                  />
                  {touched && errors.jobNumber && (
                    <FormError>{errors.jobNumber}</FormError>
                  )}
                </div>
                <div className="filter-input">
                  <TextInput
                    id="requestorFullNameContains"
                    name="requestorFullName"
                    labelText="Enquirer name contains"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.requestorFullName}
                    data-testid="requestorFullNameContains"
                  />
                  {touched && errors.requestorFullName && (
                    <FormError>{errors.requestorFullName}</FormError>
                  )}
                </div>
                <div className="filter-input">
                  <TextInput
                    id="requestorEmailContains"
                    name="requestorEmail"
                    labelText="Enquirer email contains"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.requestorEmail}
                    data-testid="requestorEmailContains"
                  />
                  {touched && errors.requestorEmail && (
                    <FormError>{errors.requestorEmail}</FormError>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="search-filter-panel-input-container">
                  <div className="filter-input">
                    <TextInput
                      id="jobNumber"
                      name="jobNumber"
                      labelText="Job number"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.jobNumber}
                      data-testid="jobNumber"
                    />
                    {touched && errors.jobNumber && (
                      <FormError>{errors.jobNumber}</FormError>
                    )}
                  </div>
                  <div className="filter-input">
                    <TextInput
                      id="userReferenceContains"
                      name="userReference"
                      labelText="User reference contains"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.userReference}
                      data-testid="userReferenceContains"
                    />
                    {touched && errors.userReference && (
                      <FormError>{errors.userReference}</FormError>
                    )}
                  </div>
                  <div className="filter-input">
                    <TextInput
                      id="addressContains"
                      name="address"
                      labelText="Address contains"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.address}
                      data-testid="addressContains"
                    />
                    {touched && errors.address && (
                      <FormError>{errors.address}</FormError>
                    )}
                  </div>
                </div>
                <hr className="divider" />
                <Toggle
                  aria-label="Only with Auto-renew pending"
                  id="auto-renew-only-toggle"
                  labelText="Only with Auto-renew pending"
                  toggled={values.isAutoRenewPending}
                  className="filter-toggle"
                  onToggle={(checked: boolean) =>
                    updateFilterValues({ isAutoRenewPending: checked })
                  }
                />
                {(user.role === USERSROLE.AOADMIN ||
                  user.role === USERSROLE.AOVIEWER) && (
                  <>
                    <hr className="divider" />
                    <Toggle
                      aria-label="Show sandbox testing enquiries"
                      id="show-sandbox-toggle"
                      labelText="Show sandbox testing enquiries"
                      toggled={values.isSandboxTest}
                      className="filter-toggle"
                      onToggle={(checked: boolean) =>
                        updateFilterValues({ isSandboxTest: checked })
                      }
                    />
                  </>
                )}
              </>
            )}
          </div>
          <div className="search-filter-panel-form-button-container">
            <Button
              kind="ghost"
              size="field"
              onClick={disabledAllExceptApply ? undefined : handleResetFilter}
              disabled={disabledAllExceptApply}
            >
              Reset
            </Button>
            {!isListView && (
              <Button
                className="search-filter-panel-form-button-apply-zoom"
                size="field"
                disabled={
                  Object.keys(errors).length > 0 || disabledAllExceptApply
                }
                onClick={
                  disabledAllExceptApply
                    ? undefined
                    : (event: any) => {
                        mapSearchWithExtentZoomRef.current = true;
                        handleSubmit(event);
                      }
                }
              >
                <ZoomInArea16 />
              </Button>
            )}
            <Button
              size="field"
              disabled={Object.keys(errors).length > 0}
              onClick={(event: any) => {
                mapSearchWithExtentZoomRef.current = false;
                handleSubmit(event);
              }}
            >
              Apply
            </Button>
          </div>
        </Form>
      </section>
    </div>
  );
};

const propTypes = {
  isFullSet: PropTypes.bool,
  handleFilter: PropTypes.func.isRequired,
  filterValues: PropTypes.object.isRequired,
  resetValues: PropTypes.func.isRequired,
  setFilterValues: PropTypes.func.isRequired,
  handleOpenCloseFilter: PropTypes.func.isRequired,
  dateRangeText: PropTypes.string,
  user: PropTypes.any,
  parentOverflow: PropTypes.bool,
  disabledAllExceptApply: PropTypes.bool,
  isListView: PropTypes.bool
};

type Props = PropTypes.InferProps<typeof propTypes>;

export default ApplyFilterForm;
